import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, from, lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IndexDBService } from './indexdb.service';
import { ApiClientService } from './api-client.service';
import { REQUEST_CONFIGS } from '../config/api-endpoints';

export interface ResourcePermissionDetail {
  status: boolean;
  msgCode: string;
  message: string;
  data: Resource[];
  Token: string;
}

export interface Resource {
  id: number;
  Name: string;
  Description?: string;
  DisplayName?: string;
  ResourceType?: string;
  ParentId?: number;
  IsActive?: boolean;
  CanView?: boolean;
  CanEdit?: boolean;
  CanDelete?: boolean;
  Children?: Resource[];
}

export interface ResourceType {
  id: number;
  Name: string;
  DisplayName: string;
  Description: string;
  IsActive: number;
  IsDeleted: number;
  UpdatedDate?: string;
  UpdatedBy?: number | null;
  ResourceCategoryID: number;
  resources: ResourceItem[];
  pinned_item?: any;
}

export interface ResourceItem {
  id: number;
  Name: string;
  Description: string;
  DisplayName: string;
  ParentId?: number | null;
  UrlRoute?: string | null;
  Params?: string | null;
  NavIconClass?: string | null;
  HideMenu: number;
  SortOrder: number;
  ResourceTypeID: number;
  IsActive: number;
  IsDeleted: number;
  UpdatedDate?: string | null;
  UpdatedBy?: number | null;
  flag: number;
  resource_permission: ResourcePermission[];
  children: ResourceItem[];
  benchmark?: any;
}

export interface ResourcePermission {
  id: number;
  UserID: number;
  RoleID?: number | null;
  GroupID?: number | null;
  ResourceID: number;
  CanView: number;
  CanEdit: number;
  CanDelete: number;
  IsActive: number;
  IsDeleted: number;
  CreatedDate: string;
  CreatedBy: number;
  UpdatedDate: string;
  UpdatedBy?: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class ResourcePermissionService {
  private readonly apiClient = inject(ApiClientService);
  private readonly indexDBService = inject(IndexDBService);

  // BehaviorSubject to store and emit resource permissions
  private resourcePermissions = new BehaviorSubject<Resource[]>([]);
  resourcePermissions$ = this.resourcePermissions.asObservable();

  constructor() {
    console.log('🚀 ResourcePermissionService initialized');
    this.initializePermissions();
  }

  /**
   * Initialize permissions by loading from IndexedDB
   */
  private async initializePermissions(): Promise<void> {
    console.log('📖 Initializing permissions from IndexedDB...');
    try {
      const storedPermissions = await this.indexDBService.getAllResourceCategories();
      console.log('📦 Retrieved stored permissions:', storedPermissions?.length || 0, 'items');
      if (storedPermissions && storedPermissions.length > 0) {
        this.resourcePermissions.next(storedPermissions);
        console.log('✅ Successfully loaded permissions from IndexedDB');
      } else {
        console.log('ℹ️ No stored permissions found in IndexedDB');
      }
    } catch (error) {
      console.error('❌ Error initializing permissions:', error);
    }
  }

  /**
   * Get resource permission details from the API and store them
   */
  async getResourcePermissionDetail(): Promise<Resource[]> {
    console.log('🔄 Fetching resource permissions from API...');
    try {
      const response = await lastValueFrom(
        this.apiClient.get<ResourcePermissionDetail>(
          `api/system/resource-permission-detail`,
          {
            ...REQUEST_CONFIGS.default,
            showLoading: false
          }
        )
      );
      console.log('📡 API Response received:', response);

      if (!response.success || !response.data?.data) {
        console.warn('⚠️ No resource permission data received from API');
        return [];
      }

      const resources = response.data.data;
      console.log('📦 Received resources from API:', resources);

      try {
        console.log('🗑️ Clearing existing resource categories...');
        await this.indexDBService.clearResourceCategories();
        console.log('✅ Cleared existing resource categories');

        console.log('💾 Storing new resources in IndexedDB...');
        await this.indexDBService.addResourceCategory(resources);
        console.log('✅ Successfully stored resources in IndexedDB');

        // Verify storage
        const storedResources = await this.indexDBService.getAllResourceCategories();
        console.log('📊 Verification - Resources in IndexedDB:', storedResources);

        // Update the BehaviorSubject
        this.resourcePermissions.next(resources);
        console.log('🔄 Updated in-memory resource permissions');
        return resources;
      } catch (dbError) {
        console.error('❌ Error storing resources in IndexedDB:', dbError);
        // Even if storage fails, return the API data
        this.resourcePermissions.next(resources);
        return resources;
      }
    } catch (apiError) {
      console.error('❌ Error fetching resource permissions:', apiError);
      // On API error, try to return cached data
      const cachedData = this.resourcePermissions.value;
      console.log('🔄 Returning cached data:', cachedData.length, 'items');
      return cachedData;
    }
  }

  /**
   * Get a resource by ID
   */
  async getResourceById(id: number): Promise<Resource | null> {
    console.log(`🔍 Fetching resource with ID ${id}...`);
    try {
      // First try to find in memory to get the Name
      const resources = this.resourcePermissions.value;
      const resource = this.findResourceById(resources, id);
      
      if (resource?.Name) {
        // If we have the Name, use it to fetch from IndexedDB
        const dbResource = await this.indexDBService.getResourceCategoryByName(resource.Name);
        if (dbResource) {
          console.log(`✅ Found resource in IndexedDB by Name:`, dbResource);
          return dbResource;
        }
      }
      
      // Fallback to ID-based index
      const idResource = await this.indexDBService.getResourceCategoryById(id);
      if (idResource) {
        console.log(`✅ Found resource in IndexedDB by ID:`, idResource);
        return idResource;
      }
      
      // If not found in IndexedDB, return the in-memory resource
      if (resource) {
        console.log(`🔍 Found resource in memory:`, resource);
        return resource;
      }
      
      console.log(`⚠️ Resource not found with ID ${id}`);
      return null;
    } catch (error) {
      console.error(`❌ Error fetching resource with ID ${id}:`, error);
      return null;
    }
  }

  /**
   * Get a resource by Name
   */
  async getResourceByName(name: string): Promise<Resource | null> {
    console.log(`🔍 Fetching resource with Name ${name}...`);
    try {
      const resource = await this.indexDBService.getResourceCategoryByName(name);
      if (resource) {
        console.log(`✅ Found resource in IndexedDB:`, resource);
        return resource;
      }

      // If not found in IndexedDB, try to find in memory
      const resources = this.resourcePermissions.value;
      const memoryResource = this.findResourceByName(resources, name);
      if (memoryResource) {
        console.log(`🔍 Found resource in memory:`, memoryResource);
        return memoryResource;
      }

      console.log(`⚠️ Resource not found with Name ${name}`);
      return null;
    } catch (error) {
      console.error(`❌ Error fetching resource with Name ${name}:`, error);
      return null;
    }
  }

  /**
   * Check if user has permission for a specific resource
   */
  hasPermission(resourceName: string, permission: 'CanView' | 'CanEdit' | 'CanDelete'): boolean {
    console.log(`🔒 Checking permission ${permission} for resource ${resourceName}`);
    const resources = this.resourcePermissions.value;
    const resource = this.findResourceByName(resources, resourceName);
    const hasPermission = resource ? resource[permission] || false : false;
    console.log(`✅ Permission check result:`, hasPermission);
    return hasPermission;
  }

  /**
   * Find a resource by name in the resource tree
   */
  private findResourceByName(resources: Resource[], name: string): Resource | null {
    for (const resource of resources) {
      if (resource.Name === name) {
        return resource;
      }
      if (resource.Children && resource.Children.length > 0) {
        const found = this.findResourceByName(resource.Children, name);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /**
   * Find a resource by ID in the resource tree
   */
  private findResourceById(resources: Resource[], id: number): Resource | null {
    for (const resource of resources) {
      if (resource.id === id) {
        return resource;
      }
      if (resource.Children && resource.Children.length > 0) {
        const found = this.findResourceById(resource.Children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }
} 